/** Matches feed volume graph — rotate when category count exceeds this. */
const CATEGORY_COUNT_ROTATE_THRESHOLD = 12;

/** Rotate when any label exceeds this length and there are enough categories to crowd the axis. */
const LONG_LABEL_CHAR_THRESHOLD = 15;

/** Minimum categories before long-label rotation applies. */
const LONG_LABEL_MIN_CATEGORIES = 2;

/** ~px per character at text-xxs for -45° end-anchored tick projection. */
const ROTATED_AXIS_CHAR_WIDTH_PX = 5.5;
const ROTATED_AXIS_BOTTOM_MIN_PX = 48;
const ROTATED_AXIS_BOTTOM_MAX_PX = 160;
const ROTATED_AXIS_LEFT_MAX_PX = 128;
const DEFAULT_AXIS_MARGIN_PX = 12;

export interface CategoryAxisLayout {
  shouldRotate: boolean;
  margins: { bottom: number; left: number };
  /** Margins for -45° ticks; use when rotation is forced via `rotateXAxisTick` without auto-rotate. */
  rotatedMargins: { bottom: number; left: number };
  maxLabelLength: number;
}

const maxCategoryLabelLength = (labels: string[]): number =>
  labels.length === 0 ? 0 : Math.max(...labels.map((label) => label.length));

const shouldRotateLabels = (
  labels: string[],
  maxLabelLength: number,
): boolean =>
  labels.length > 0 &&
  (labels.length > CATEGORY_COUNT_ROTATE_THRESHOLD ||
    (maxLabelLength > LONG_LABEL_CHAR_THRESHOLD &&
      labels.length >= LONG_LABEL_MIN_CATEGORIES));

const rotatedLabelProjection = (maxLabelLength: number): number =>
  Math.ceil(maxLabelLength * ROTATED_AXIS_CHAR_WIDTH_PX * Math.SQRT1_2);

const rotatedMarginsForMaxLabelLength = (
  maxLabelLength: number,
  hasLabels: boolean,
): { bottom: number; left: number } => {
  if (!hasLabels) {
    return { bottom: ROTATED_AXIS_BOTTOM_MIN_PX, left: DEFAULT_AXIS_MARGIN_PX };
  }
  const projected = rotatedLabelProjection(maxLabelLength);
  return {
    bottom: Math.min(
      ROTATED_AXIS_BOTTOM_MAX_PX,
      Math.max(ROTATED_AXIS_BOTTOM_MIN_PX, projected + 12),
    ),
    left: Math.min(
      ROTATED_AXIS_LEFT_MAX_PX,
      Math.max(DEFAULT_AXIS_MARGIN_PX, projected),
    ),
  };
};

/**
 * Single-pass category axis layout: rotation heuristic, margins, and longest label length.
 * @param {string[]} labels - Category axis tick labels.
 * @returns {CategoryAxisLayout} Rotation flag, chart margins, and max label length.
 */
export const resolveCategoryAxisLayout = (
  labels: string[],
): CategoryAxisLayout => {
  const maxLabelLength = maxCategoryLabelLength(labels);
  const shouldRotate = shouldRotateLabels(labels, maxLabelLength);
  const rotatedMargins = rotatedMarginsForMaxLabelLength(
    maxLabelLength,
    labels.length > 0,
  );
  const margins = shouldRotate
    ? rotatedMargins
    : {
        bottom: DEFAULT_AXIS_MARGIN_PX,
        left: DEFAULT_AXIS_MARGIN_PX,
      };

  return { shouldRotate, margins, rotatedMargins, maxLabelLength };
};

/**
 * Returns a Recharts tick formatter; full label text unless `maxChars` caps length.
 * @param {number} [maxChars] - Positive max length; omit or non-positive for full strings.
 * @returns {(value: unknown) => string} Tick formatter for category axes.
 */
export const formatCategoryAxisTick =
  (maxChars?: number) =>
  (value: unknown): string => {
    const s = String(value);
    if (maxChars == null || maxChars <= 0) return s;
    return s.slice(0, maxChars);
  };
